/**
 * Shift a number's decimal point by `places` through its decimal string form, so the point
 * moves in decimal rather than in binary: `4.725 * 100` is 472.49999999999994, while
 * `Number("4.725e2")` is 472.5. Handles a string that already carries an exponent.
 */
const shiftDecimal = (value: number, places: number): number => {
  const [mantissa, exponent] = String(value).split("e");
  return Number(`${mantissa}e${Number(exponent ?? 0) + places}`);
};

/** Significant digits an IEEE-754 double represents exactly in every case. */
const SIGNIFICANT_DIGITS = 15;

/**
 * Round money half away from zero — a port of the backend's `roundHalfUp`
 * (`src/utils/number.util.ts`), so a total previewed here is the figure the server stores.
 *
 * `Math.round(x * 100) / 100` rounds 4.725 down to 4.72 because x.xx5 is not representable in
 * binary; this trims float noise to 15 significant digits and then rounds the decimal digits.
 * Anything non-finite returns 0, and the result is never -0.
 */
export const roundHalfUp = (value: number, dp = 2): number => {
  if (!Number.isFinite(value)) return 0;
  const cleaned = Number(Math.abs(value).toPrecision(SIGNIFICANT_DIGITS));
  const magnitude = shiftDecimal(Math.round(shiftDecimal(cleaned, dp)), -dp);
  if (magnitude === 0) return 0;
  return value < 0 ? -magnitude : magnitude;
};
